import { useEffect, useMemo, useRef, useState } from 'react';
import { useEstimateLineItemState } from '@/hooks/estimates/useEstimateLineItemState';
import { apiPost, operationsClient } from '@/lib/api';
import {
  computeLinePreview,
  type PricebookLineItem,
  type EditableLineItem,
} from '@/routes/_dashboard/dashboard/estimates/_shared';
import type { TaxResolutionResult } from '@/types/tax';
import { reconciledTaxBreakdown } from '@/lib/tax/tax-utils';

function toPricebookLineItem(item: Record<string, unknown>): PricebookLineItem {
  return {
    id: String(item.lineItemId ?? item.id ?? ''),
    itemVersion: Number(item.itemVersion ?? 1),
    name: String(item.name ?? ''),
    code: String(item.code ?? ''),
    type: String(item.type ?? ''),
    unit: String(item.unit ?? 'each'),
    description: item.description ? String(item.description) : undefined,
    basePrice: Number(item.basePrice ?? 0),
    laborRate: Number(item.laborRate ?? 0),
    costBasis: Number(item.costBasis ?? 0),
    markupPercent: Number(item.markupPercent ?? 0),
    priceStrategy: String(item.priceStrategy ?? 'FLAT'),
    taxable: Boolean(item.taxable ?? true),
    taxLineType: item.taxLineType != null ? String(item.taxLineType) : null,
  };
}

export function useEstimateLiteLineItems(
  taxResolution: TaxResolutionResult | null,
  pricingMode: 'STANDARD' | 'TM_OVERRIDE' = 'STANDARD',
) {
  const { lineItems, setLineItems, addLineItem, removeLineItem, updateQuantity } =
    useEstimateLineItemState();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PricebookLineItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const raw = await apiPost<
          { results?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>
        >(operationsClient, '/pricebook/line-items/search-hybrid', {
          queryText: searchQuery.trim(),
          limit: 10,
        });
        const arr = Array.isArray(raw)
          ? raw
          : ((raw as { results?: Array<Record<string, unknown>> }).results ?? []);
        const list = arr.map(toPricebookLineItem);
        setSearchResults(list);
        setShowSearchResults(list.length > 0);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  useEffect(() => {
    function handler(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function addItem(item: PricebookLineItem) {
    addLineItem(item);
    setSearchQuery('');
    setShowSearchResults(false);
  }

  const lineItemPreviews = useMemo(
    () => lineItems.map((item) => computeLinePreview(item, pricingMode, taxResolution)),
    [lineItems, pricingMode, taxResolution],
  );
  const subtotal = useMemo(
    () => lineItemPreviews.reduce((sum, preview) => sum + preview.lineSubtotal, 0),
    [lineItemPreviews],
  );
  const taxTotal = useMemo(
    () => lineItemPreviews.reduce((sum, preview) => sum + preview.lineTax, 0),
    [lineItemPreviews],
  );
  const taxBreakdown = useMemo(
    () => reconciledTaxBreakdown(lineItemPreviews, taxTotal),
    [lineItemPreviews, taxTotal],
  );
  const grandTotal = subtotal + taxTotal;

  return {
    lineItems,
    setLineItems,
    removeLineItem,
    updateQuantity,
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    showSearchResults,
    setShowSearchResults,
    searchRef,
    addItem,
    lineItemPreviews,
    subtotal,
    taxTotal,
    taxBreakdown,
    grandTotal,
  };
}

export type EstimateLiteLineItemsState = ReturnType<typeof useEstimateLiteLineItems>;
export type EstimateLiteLineItem = EditableLineItem;
